<script lang="ts">
    // Escolher UMA entidade do sistema — pessoa, serviço, usuário, conta, categoria... Um componente
    // só para todas: o que muda de uma para outra (como buscar, o que mostrar em cada linha, como
    // criar uma nova) está descrito em zentity-select.ts, não aqui.
    //
    //   <ZentitySelect fonte="pessoa" label="Cliente" bind:value={entity.person} />
    //   <ZentitySelect fonte="servico" bind:value={item.service} params={{ type: 'all', price: true }} />
    //
    // `value` é sempre a ENTIDADE (o objeto), nunca o id — é o que as telas já usavam nos seletores
    // antigos, e evita a dança de guardar id e objeto em paralelo.
    import { createEventDispatcher } from 'svelte'
    import { faPlus } from '@fortawesome/free-solid-svg-icons'
    import Zinput from './zinput.svelte'
    import ZinputSearchTrigger from './zinput/zinput-search-trigger.svelte'
    import Zoption from './zoption.svelte'
    import Popover from '../popover.svelte'
    import type { GridButton } from './zgrid'
    import { entitySources } from './zentity-select'
    import type { EntityCreateOption } from './zentity-select'

    const dispatch = createEventDispatcher()

    let className = ''
    export { className as class }
    /** chave de zentity-select.ts: pessoa, servico, usuario, conta, categoriaFinanceira... */
    export let fonte: string
    export let value: any = null
    export let label: string = null
    export let disabled: boolean = false
    /** filtros e opções da fonte: { bank }, { creditDebit }, { type, price, duration, scheduleId } */
    export let params: any = {}
    export let showClear: boolean = true
    /** modo lista: restringe a escolha a estes ids (ex.: só os atendentes da agenda) */
    export let somenteIds: string[] = null
    /** modo lista: rótulo da opção que representa "nenhum"/"todos" em campos de filtro */
    export let opcaoVazia: string = null

    $: origem = entitySources[fonte]
    $: rotulo = label ?? origem?.nome ?? ''
    $: id = value ? origem?.id(value) : null
    $: limpavel = showClear && !disabled && (!origem?.podeLimpar || !value || origem.podeLimpar(value))

    // ---- modo lista: carrega uma vez e escolhe num select ------------------------------------
    let itens: any[] = []
    $: disponiveis = somenteIds?.length ? itens.filter((el) => somenteIds.includes(origem.id(el))) : itens
    $: opcoes = [
        ...(opcaoVazia ? [{ value: null, label: opcaoVazia }] : []),
        ...disponiveis.map((el) => ({
            value: origem.id(el),
            label: origem.titulo(el, params),
            disabled: origem.indisponivel ? origem.indisponivel(el, params) : false,
        })),
    ]

    let carregado = false
    async function carregar() {
        if (origem?.modo !== 'lista') return
        carregado = false
        itens = (await origem.carregar(params)) || []
        carregado = true
    }

    // Uma opção só (empresa com um atendente, agenda com um atendente): obrigar a escolher é atrito sem
    // ganho. Precisa ser reativo a `disponiveis`, não um if dentro do `carregar`: ali `disponiveis` ainda é
    // a lista anterior (o `$:` só recalcula no flush) e o `somenteIds` costuma chegar depois — a agenda do
    // agendamento é carregada em paralelo com o formulário. Campo de filtro (`opcaoVazia`) fica de fora:
    // ali o padrão é "Todos", e escolher sozinho mudaria o resultado da tela.
    $: escolherSeForUnico(carregado, disponiveis)
    function escolherSeForUnico(_carregado: boolean, _disponiveis: any[]) {
        if (!_carregado || !origem?.escolherSeUnico || opcaoVazia || value) return
        if (_disponiveis.length !== 1) return
        value = _disponiveis[0]
        dispatch('change', value)
    }

    // A lista pode depender do contexto (as vendas são de UMA pessoa; o serviço filtra ainda mais).
    // Quando esse contexto muda, a lista carregada fica velha — então recarrega. Comparação por
    // JSON porque `params` costuma ser um literal novo a cada render do pai.
    let ultimoContexto: string = null
    $: recarregarSeMudou(fonte, params)
    function recarregarSeMudou(_fonte: string, _params: any) {
        if (entitySources[_fonte]?.modo !== 'lista') return
        const agora = _fonte + '|' + JSON.stringify(_params ?? {})
        if (agora === ultimoContexto) return
        ultimoContexto = agora
        carregar()
    }

    function onSelectChange(ev) {
        const escolhido = ev?.detail?.value ?? ev?.detail
        value = disponiveis.find((el) => origem.id(el) === escolhido) || null
        dispatch('change', value)
    }

    // ---- modo busca: consulta o servidor ------------------------------------------------------
    let query = ''
    let resultados: any[] = []

    async function buscar(q: string) {
        resultados = (await origem.buscar(q, params)) || []
    }

    async function escolher(item: any, fechar?: () => void) {
        value = item
        query = ''
        resultados = []
        fechar?.()
        // o campo já mostra o escolhido; a fonte ainda pode precisar completar o registro (relação que a
        // busca não traz). o `change` sai só depois, para a tela nunca receber a entidade pela metade.
        if (origem?.aoEscolher) value = await origem.aoEscolher(item, params)
        dispatch('change', value)
    }

    // a tela pode entregar a entidade só com o id (veio de um DTO): busca o resto uma vez.
    // createdAt só existe no registro vindo do servidor — é o mesmo teste que os seletores antigos
    // faziam para decidir se precisavam hidratar.
    let hidratando: string = null
    async function hidratar(_id: string) {
        if (!origem?.obter || !_id || hidratando === _id) return
        hidratando = _id
        value = await origem.obter(_id, params)
    }

    $: if (origem?.modo === 'busca' && id && !value?.createdAt) hidratar(id)

    function limpar() {
        value = null
        dispatch('change', null)
        dispatch('clear')
    }

    // ---- criar um registro novo --------------------------------------------------------------
    let editor: any = null
    let editorOpcao: EntityCreateOption = null
    let editorProps: any = {}
    let menuCriar: EntityCreateOption[] = null

    // com algo escolhido não há o que cadastrar: o "+" some e fica só a ação sobre o escolhido
    $: podeCriar = !disabled && !value && !!origem?.criar?.permitido()
    // com um registro escolhido, a fonte pode oferecer uma ação sobre ELE — hoje é o botão que abre a
    // ficha da pessoa, que existia nos seletores antigos. Vem antes do "+" porque age sobre o que está ali.
    $: podeAbrir = !!value && !!origem?.abrir?.permitido()
    $: acts = [
        ...(podeAbrir ? [{ callback: () => origem.abrir.acao(value, params), label: null, title: origem.abrir.titulo, icon: origem.abrir.icon }] : []),
        ...(podeCriar ? [{ callback: abrirCriar, label: null, title: 'Cadastrar ' + (origem?.nome || '').toLowerCase(), icon: faPlus, color: 'green' }] : []),
    ] as GridButton[]

    function abrirCriar() {
        const opcoes = origem.criar.opcoes(params) || []
        if (opcoes.length === 0) return
        // um editor só: abre direto. Vários (ex.: catálogo do PDV, que aceita serviço, produto e
        // insumo): o usuário escolhe qual está criando.
        if (opcoes.length === 1) return abrirEditor(opcoes[0])
        menuCriar = opcoes
    }

    async function abrirEditor(opcao: EntityCreateOption) {
        menuCriar = null
        editorOpcao = opcao
        editorProps = opcao.props(origem.criar.vazio(), params)
        const mod = await opcao.componente()
        editor = mod.default
    }

    async function fecharEditor(ev) {
        editor = null
        const salvo = origem.criar.doEvento(ev?.detail)
        if (origem.modo === 'lista') await carregar()
        if (salvo) {
            value = salvo
            dispatch('change', value)
        }
    }

</script>

{#if menuCriar}
    <Popover on:close={() => (menuCriar = null)} title="O que deseja cadastrar?" width="420px" height="80px">
        <div class="zentity_menu">
            {#each menuCriar as opcao}
                <Zoption title={opcao.rotulo || opcao.titulo} on:click={() => abrirEditor(opcao)} />
            {/each}
        </div>
    </Popover>
{/if}

{#if editor}
    <Popover on:close={fecharEditor} title={editorOpcao.titulo} width={editorOpcao.largura} height={editorOpcao.altura}>
        <svelte:component this={editor} {...editorProps} on:close={fecharEditor} />
    </Popover>
{/if}

<!-- no papel um campo não serve de nada: sai o rótulo e o nome do que está escolhido, em texto.
     Era um truque feito à mão dentro do seletor de venda; aqui vale para qualquer entidade. -->
<div class="zentity_print h-screen">
    {#if rotulo}<span class="zentity_print_label">{rotulo}:</span>{/if}
    {value ? origem?.titulo(value, params) : '-'}
</div>

<div class="zentity h-print {className}">
    {#if origem?.modo === 'lista'}
        <Zinput type="select" label={rotulo} {disabled} {acts} options={opcoes} value={id} showClear={limpavel} on:change={onSelectChange} on:clear={limpar} />
    {:else if value && id}
        <!-- escolhido: o campo de busca sai de cena e fica o nome, com o "x" para trocar -->
        <Zinput type="text" label={rotulo} {acts} value={origem.titulo(value, params)} disabled={true} showClear={limpavel} on:clear={limpar} />
    {:else}
        <ZinputSearchTrigger bind:query label={rotulo} {disabled} {acts} minLength={origem?.minimo ?? 3} search={buscar} on:clear={limpar} let:close let:tooShort>
            {#if resultados.length === 0}
                <div class="zentity_dica">
                    {#if tooShort && origem?.dicaBusca}
                        {origem.dicaBusca}
                    {:else}
                        Nada encontrado.
                    {/if}
                </div>
            {:else}
                {#each resultados as item (origem.id(item))}
                    <Zoption
                        title={origem.titulo(item, params)}
                        details={origem.detalhes ? origem.detalhes(item, params) : []}
                        badge={origem.etiqueta ? origem.etiqueta(item) : ''}
                        badgeTone={origem.etiquetaTom ? origem.etiquetaTom(item) : 'neutral'}
                        on:click={() => escolher(item, close)}
                    />
                {/each}
            {/if}
        </ZinputSearchTrigger>
    {/if}
</div>

<style>
    .zentity {
        flex-grow: 1;
        position: relative;
        min-width: 0;
    }
    .zentity_dica {
        padding: 6px 12px;
        opacity: 0.7;
        white-space: pre-line;
    }
    .zentity_menu {
        display: flex;
        flex-direction: column;
        padding: 8px;
    }
    .zentity_print_label {
        opacity: 0.7;
        margin-right: 4px;
    }
</style>
